// src/lib/ErrorBoundary.jsx
// React error boundary that reports to Sentry and renders a safe fallback UI.
// Lives in src/lib/ so strict mode applies. Consumers import and wrap route trees
// or specific subtrees where a crash should not take down the whole app.
//
// Pair with src/lib/sentry-client.js — initSentryClient() must run before the first
// boundary mounts, otherwise reports are dropped.

import React from 'react'
import { captureClientException } from './sentry-client.js'

/**
 * @typedef {Object} ErrorBoundaryProps
 * @property {React.ReactNode} children
 * @property {React.ReactNode | ((error: Error, reset: () => void) => React.ReactNode)} [fallback]  - Custom fallback UI or render-prop. Default: minimal error card.
 * @property {string} [component]  - Logical component label for Sentry tag (e.g. 'StaffLayout', 'ClientPortal')
 * @property {() => void} [onReset]  - Called when the user clicks "Try again" (default fallback only)
 */

/**
 * @typedef {Object} ErrorBoundaryState
 * @property {Error | null} error
 */

/** @extends {React.Component<ErrorBoundaryProps, ErrorBoundaryState>} */
export class ErrorBoundary extends React.Component {
  /** @param {ErrorBoundaryProps} props */
  constructor(props) {
    super(props)
    /** @type {ErrorBoundaryState} */
    this.state = { error: null }
    this.handleReset = this.handleReset.bind(this)
  }

  /**
   * @param {Error} error
   * @returns {ErrorBoundaryState}
   */
  static getDerivedStateFromError(error) {
    return { error }
  }

  /**
   * @param {Error} error
   * @param {React.ErrorInfo} errorInfo
   */
  componentDidCatch(error, errorInfo) {
    captureClientException(error, {
      component: this.props.component || 'ErrorBoundary',
      action: 'render',
      extra: {
        componentStack: errorInfo.componentStack,
      },
    })
  }

  handleReset() {
    this.setState({ error: null })
    if (this.props.onReset) this.props.onReset()
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    const { fallback } = this.props
    if (typeof fallback === 'function') {
      return fallback(error, this.handleReset)
    }
    if (fallback !== undefined) {
      return fallback
    }

    // Default fallback — minimal, themed with existing app tokens so it fits whatever surface it mounts in
    return (
      <div style={{
        maxWidth: '42rem',
        margin: '2rem auto',
        padding: '1.5rem',
        border: '1px solid var(--color-border, #e5e7eb)',
        borderRadius: '0.5rem',
        background: 'var(--color-card, #fff)',
        color: 'var(--color-text, #111827)',
      }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          Something went wrong
        </h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-dim, #6b7280)', marginBottom: '1rem' }}>
          The error has been reported. You can try again, or reload the page.
        </p>
        {import.meta.env.DEV && (
          <pre style={{
            fontSize: '0.75rem',
            padding: '0.75rem',
            background: 'var(--bg-subtle, #f9fafb)',
            borderRadius: '0.25rem',
            overflow: 'auto',
            marginBottom: '1rem',
            color: 'var(--color-text-dim, #6b7280)',
          }}>{error.message}</pre>
        )}
        <button
          onClick={this.handleReset}
          style={{
            padding: '0.5rem 1rem',
            background: 'var(--color-primary, #2563eb)',
            color: '#fff',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontSize: '0.875rem',
          }}
        >
          Try again
        </button>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.5rem 1rem',
            background: 'transparent',
            color: 'var(--color-text, #111827)',
            border: '1px solid var(--color-border, #e5e7eb)',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontSize: '0.875rem',
            marginLeft: '0.5rem',
          }}
        >
          Reload page
        </button>
      </div>
    )
  }
}
